import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const TOKEN_KEY = "pixtection_admin_token";

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso.endsWith("Z") ? iso : iso + "Z");
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}
function fmtRelative(iso) {
  if (!iso) return null;
  const d = new Date(iso.endsWith("Z") ? iso : iso + "Z");
  const diff = Math.round((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}
function fmtDuration(s) {
  if (s == null) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}

function StatCard({ label, value, sub, color = "blue" }) {
  const colors = {
    blue: "from-blue-500 to-blue-700", green: "from-green-500 to-green-700",
    red: "from-red-500 to-red-700", yellow: "from-yellow-500 to-yellow-600",
    purple: "from-purple-500 to-purple-700",
  };
  return (
    <div className={`rounded-xl p-5 text-white bg-gradient-to-br ${colors[color]} shadow-lg`}>
      <p className="text-sm font-medium opacity-80">{label}</p>
      <p className="text-4xl font-bold mt-1">{value ?? "—"}</p>
      {sub && <p className="text-xs opacity-70 mt-1">{sub}</p>}
    </div>
  );
}

function Bar({ pct, color = "bg-green-500", bg = "bg-gray-700", height = "h-1.5" }) {
  return (
    <div className={`w-full ${bg} rounded-full ${height} overflow-hidden`}>
      <div className={`${height} ${color} rounded-full transition-all duration-500`}
        style={{ width: `${Math.min(100, Math.max(0, pct ?? 0))}%` }} />
    </div>
  );
}

// Quota status bar for a single key
function QuotaBar({ label, used, limit, remaining }) {
  if (limit == null) return null;
  const pct = limit > 0 ? Math.round(((used ?? 0) / limit) * 100) : 0;
  const exhausted = remaining === 0;
  const color = exhausted ? "bg-red-500" : pct > 80 ? "bg-yellow-500" : "bg-green-500";
  return (
    <div>
      <div className="flex justify-between text-xs text-gray-500 mb-0.5">
        <span>{label}</span>
        <span className={exhausted ? "text-red-400 font-bold" : ""}>
          {used ?? "?"} / {limit} used
          {remaining != null && <span className="text-gray-500 ml-1">({remaining} left)</span>}
        </span>
      </div>
      <Bar pct={pct} color={color} height="h-2" />
    </div>
  );
}

function KeyCard({ k, probeResult, quotaData, onRemove, removing }) {
  const [open, setOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const total = k.total_requests || 0;
  const success = k.success_count || 0;
  const errors = k.total_429s || 0;
  const healthPct = total > 0 ? Math.round((success / total) * 100) : null;

  // Use quota data if available
  const q = quotaData || {};
  const qDaily = q.daily || {};
  const qRpm = q.rpm || {};
  const qStatus = q.status;
  const isExhausted = qStatus === "daily_exhausted";
  const isRpmLimited = qStatus === "rpm_limited";

  let badge, badgeCls;
  if (isExhausted) {
    badge = "🚫 Daily quota exhausted";
    badgeCls = "bg-red-900/80 text-red-300 border border-red-600";
  } else if (isRpmLimited) {
    badge = `⏳ RPM limited (${fmtDuration(q.retry_in_s)})`;
    badgeCls = "bg-yellow-900/80 text-yellow-300 border border-yellow-600";
  } else if (!k.available) {
    badge = `⏳ Cooldown ${k.cooldown_remaining_s}s`;
    badgeCls = "bg-red-900/70 text-red-300 border border-red-700";
  } else if (qStatus === "healthy") {
    badge = "✅ Healthy — quota OK";
    badgeCls = "bg-green-900/70 text-green-300 border border-green-700";
  } else if (k.quota_ok === false) {
    badge = "⚠️ Quota issue";
    badgeCls = "bg-yellow-900/70 text-yellow-300 border border-yellow-700";
  } else if (k.quota_ok === true) {
    badge = "✅ Healthy";
    badgeCls = "bg-green-900/70 text-green-300 border border-green-700";
  } else if (total === 0) {
    badge = "⬜ Unused";
    badgeCls = "bg-gray-800 text-gray-400 border border-gray-700";
  } else {
    badge = "✅ Available";
    badgeCls = "bg-green-900/70 text-green-300 border border-green-700";
  }

  const barColor =
    isExhausted ? "bg-red-500" :
    !k.available ? "bg-red-500" :
    healthPct === null ? "bg-gray-500" :
    healthPct >= 80 ? "bg-green-500" :
    healthPct >= 50 ? "bg-yellow-500" : "bg-red-500";

  return (
    <div className={`bg-gray-900 border rounded-xl overflow-hidden transition-all duration-200 ${
      isExhausted ? "border-red-700" : !k.available ? "border-red-800" :
      k.quota_ok === false ? "border-yellow-800" : "border-gray-800"
    }`}>
      <button className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-800/50 transition"
        onClick={() => setOpen((o) => !o)}>
        <span className="font-mono text-sm text-gray-300 w-24 shrink-0">{k.key_suffix}</span>
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 max-w-[220px] ${badgeCls}`}>{badge}</span>

        {/* Mini quota bars inline */}
        {quotaData && (
          <div className="flex-1 min-w-0 hidden lg:flex flex-col gap-0.5">
            {qDaily.limit != null && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-gray-500 w-10">Daily</span>
                <div className="flex-1 h-1 bg-gray-700 rounded-full overflow-hidden">
                  <div className={`h-1 rounded-full ${qDaily.exhausted ? "bg-red-500" : "bg-green-500"}`}
                    style={{ width: `${qDaily.limit > 0 ? Math.round(((qDaily.used ?? 0) / qDaily.limit) * 100) : 0}%` }} />
                </div>
                <span className="text-[10px] text-gray-500 w-20 text-right">
                  {qDaily.used ?? "?"}/{qDaily.limit}
                </span>
              </div>
            )}
            {qRpm.limit != null && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-gray-500 w-10">RPM</span>
                <div className="flex-1 h-1 bg-gray-700 rounded-full overflow-hidden">
                  <div className={`h-1 rounded-full ${qRpm.exhausted ? "bg-red-500" : "bg-green-500"}`}
                    style={{ width: `${qRpm.limit > 0 ? Math.round(((qRpm.used ?? 0) / qRpm.limit) * 100) : 0}%` }} />
                </div>
                <span className="text-[10px] text-gray-500 w-20 text-right">
                  {qRpm.used ?? "?"}/{qRpm.limit}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Fallback health bar if no quota data */}
        {!quotaData && (
          <div className="flex-1 min-w-0 hidden md:block">
            {total > 0 ? (
              <div className="space-y-0.5">
                <Bar pct={healthPct} color={barColor} />
                <p className="text-xs text-gray-500">{success} ok · {errors} quota · {healthPct ?? "—"}%</p>
              </div>
            ) : <p className="text-xs text-gray-600">No requests yet</p>}
          </div>
        )}

        {k.quota_retry_in_s != null && !k.available && (
          <span className="text-xs text-red-400 shrink-0 hidden lg:block">↻ {fmtDuration(k.quota_retry_in_s)}</span>
        )}
        <span className="text-xs text-gray-600 shrink-0 hidden md:block">
          {k.last_used_at ? fmtRelative(k.last_used_at) : "never"}
        </span>
        <span className="text-gray-600 text-xs shrink-0">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="border-t border-gray-800 px-4 py-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-500 text-xs mb-1">Total Requests</p>
            <p className="text-white font-mono text-lg font-bold">{total}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs mb-1">Successes</p>
            <p className="text-green-400 font-mono text-lg font-bold">{success}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs mb-1">429 Errors</p>
            <p className={`font-mono text-lg font-bold ${errors > 0 ? "text-red-400" : "text-gray-500"}`}>{errors}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs mb-1">Health</p>
            <p className={`font-mono text-lg font-bold ${
              healthPct === null ? "text-gray-500" : healthPct >= 80 ? "text-green-400" :
              healthPct >= 50 ? "text-yellow-400" : "text-red-400"
            }`}>{healthPct !== null ? `${healthPct}%` : "—"}</p>
          </div>

          {/* Quota detail */}
          {quotaData && (
            <div className="col-span-2 md:col-span-4 bg-gray-800/50 rounded-lg px-3 py-2 space-y-2">
              <p className="text-gray-400 text-xs font-semibold">Quota Status</p>
              <QuotaBar label="📅 Daily Requests" used={qDaily.used} limit={qDaily.limit} remaining={qDaily.remaining} />
              <QuotaBar label="⚡ RPM (Requests/Min)" used={qRpm.used} limit={qRpm.limit} remaining={qRpm.remaining} />
              {q.retry_in_s != null && !quotaData.alive && (
                <p className="text-xs text-red-400">Retry in {fmtDuration(q.retry_in_s)}</p>
              )}
            </div>
          )}

          <div>
            <p className="text-gray-500 text-xs mb-1">Last Used</p>
            <p className="text-gray-300 font-mono text-xs">{k.last_used_at ? `${fmtTime(k.last_used_at)} (${fmtRelative(k.last_used_at)})` : "—"}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs mb-1">Last Success</p>
            <p className="text-gray-300 font-mono text-xs">{k.last_success_at ? `${fmtTime(k.last_success_at)} (${fmtRelative(k.last_success_at)})` : "—"}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs mb-1">Last Error</p>
            <p className="text-gray-300 font-mono text-xs">{k.last_error_at ? `${fmtTime(k.last_error_at)} (${fmtRelative(k.last_error_at)})` : "—"}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs mb-1">Cooldown Until</p>
            <p className="text-gray-300 font-mono text-xs">{k.cooldown_ends_at ? fmtTime(k.cooldown_ends_at) : "—"}</p>
          </div>

          {k.last_error_msg && (
            <div className="col-span-2 md:col-span-4">
              <p className="text-gray-500 text-xs mb-1">Last Error</p>
              <p className="text-red-400 text-xs font-mono bg-red-950/30 rounded px-2 py-1 break-words">{k.last_error_msg}</p>
            </div>
          )}

          {quotaData && !quotaData.alive && quotaData.error && (
            <div className="col-span-2 md:col-span-4">
              <p className="text-gray-500 text-xs mb-1">Probe Error</p>
              <p className="text-red-400 text-xs font-mono bg-red-950/30 rounded px-2 py-1 break-words">{quotaData.error}</p>
            </div>
          )}
          {/* Remove key button */}
          <div className="col-span-2 md:col-span-4 flex justify-end pt-2 border-t border-gray-800 mt-2">
            {confirmRemove ? (
              <div className="flex items-center gap-2">
                <span className="text-red-400 text-xs">Remove this key?</span>
                <button
                  onClick={() => onRemove(k.key_suffix)}
                  disabled={removing}
                  className="bg-red-700 hover:bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition"
                >
                  {removing ? "Removing…" : "Yes, Remove"}
                </button>
                <button
                  onClick={() => setConfirmRemove(false)}
                  className="bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs px-3 py-1.5 rounded-lg transition"
                >Cancel</button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmRemove(true)}
                className="text-gray-600 hover:text-red-400 text-xs flex items-center gap-1 transition"
              >
                🗑️ Remove Key
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [keypool, setKeypool] = useState(null);
  const [quotaData, setQuotaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingQuota, setFetchingQuota] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);

  const apiFetch = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API}${path}`, {
      ...opts, headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...(opts.headers || {}) },
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.json();
  }, [token]);

  const loadData = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [s, k] = await Promise.all([apiFetch("/admin/stats"), apiFetch("/admin/keypool")]);
      setStats(s); setKeypool(k); setLastRefresh(new Date()); setAuthed(true);
    } catch (e) {
      if (e.message.includes("401") || e.message.includes("503")) { setAuthed(false); setError("Invalid admin token."); }
      else { setError(`Error: ${e.message}`); }
    } finally { setLoading(false); }
  }, [apiFetch]);

  const handleFetchQuota = async () => {
    setFetchingQuota(true); setMsg("");
    try {
      const q = await apiFetch("/admin/fetch-quota", { method: "POST" });
      setQuotaData(q);
      // Also refresh keypool to sync probe state
      const k = await apiFetch("/admin/keypool");
      setKeypool(k);

      const alive = q.keys_alive;
      const dead = q.keys_daily_exhausted;
      const rpm = q.keys_rpm_limited;
      const agg = q.aggregate_daily || {};

      if (dead === q.total_keys) {
        setMsg(`🚫 All ${q.total_keys} keys daily quota exhausted. Pool: ${agg.used ?? "?"}/${agg.limit ?? "?"} daily requests used. Resets at midnight Pacific.`);
      } else if (alive === q.total_keys) {
        setMsg(`✅ All ${alive} keys healthy! Pool: ${agg.used ?? 0}/${agg.limit ?? "?"} daily requests used.`);
      } else {
        const parts = [];
        if (alive > 0) parts.push(`${alive} alive`);
        if (dead > 0) parts.push(`${dead} daily exhausted`);
        if (rpm > 0) parts.push(`${rpm} RPM limited`);
        setMsg(`📊 ${parts.join(" · ")} | Pool: ${agg.used ?? "?"}/${agg.limit ?? "?"} daily used`);
      }
    } catch (e) {
      setMsg(`❌ Quota fetch failed: ${e.message}`);
    } finally { setFetchingQuota(false); }
  };

  const handleLogin = (e) => { e.preventDefault(); localStorage.setItem(TOKEN_KEY, input); setToken(input); };
  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY); setToken(""); setInput(""); setAuthed(false);
    setStats(null); setKeypool(null); setQuotaData(null);
  };
  const handlePurge = async () => {
    if (!window.confirm("Purge all stored image payloads?")) return;
    try { const r = await apiFetch("/admin/purge-images", { method: "POST" }); setMsg(`✅ Purged ${r.modified_count} records`); }
    catch (e) { setMsg(`❌ ${e.message}`); }
  };
  const handleClearHistory = async () => {
    if (!window.confirm("⚠️ Delete ALL history?")) return;
    try { const r = await apiFetch("/admin/history", { method: "DELETE" }); setMsg(`✅ Deleted ${r.deleted_count} records`); loadData(); }
    catch (e) { setMsg(`❌ ${e.message}`); }
  };

  // Key management state
  const [newKey, setNewKey] = useState("");
  const [addingKey, setAddingKey] = useState(false);
  const [removingKey, setRemovingKey] = useState(null); // suffix being removed
  const [showKeyInput, setShowKeyInput] = useState(false);

  const handleAddKey = async () => {
    const k = newKey.trim();
    if (!k) return;
    setAddingKey(true); setMsg("");
    try {
      const r = await apiFetch("/admin/add-key", { method: "POST", body: JSON.stringify({ key: k }) });
      setMsg(`✅ Added key ${r.key_suffix}. Pool now has ${r.total_keys} keys.`);
      setNewKey(""); setShowKeyInput(false);
      setKeypool({ total_keys: r.total_keys, keys: r.keys });
    } catch (e) {
      setMsg(`❌ Add key failed: ${e.message}`);
    } finally { setAddingKey(false); }
  };

  const handleRemoveKey = async (suffix) => {
    const bare = suffix.replace(/^\.+/ , "").trim();
    if (!window.confirm(`Remove key ${suffix}? This cannot be undone.`)) return;
    setRemovingKey(bare); setMsg("");
    try {
      const r = await apiFetch("/admin/remove-key", { method: "DELETE", body: JSON.stringify({ suffix: bare }) });
      setMsg(`✅ Removed key ${r.removed_suffix}. Pool now has ${r.total_keys} keys.`);
      setKeypool({ total_keys: r.total_keys, keys: r.keys });
    } catch (e) {
      setMsg(`❌ Remove key failed: ${e.message}`);
    } finally { setRemovingKey(null); }
  };

  useEffect(() => { if (token) loadData(); }, [token, loadData]);
  useEffect(() => { if (!authed) return; const id = setInterval(loadData, 30000); return () => clearInterval(id); }, [authed, loadData]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="bg-gray-900 rounded-2xl shadow-2xl p-8 w-full max-w-sm border border-gray-800">
          <div className="text-center mb-6">
            <div className="text-4xl mb-2">🔐</div>
            <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
            <p className="text-gray-400 text-sm mt-1">ai-detect.teq.la</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="password" value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Enter admin token..." autoFocus
              className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 border border-gray-700 focus:outline-none focus:border-blue-500 transition" />
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition">Login</button>
          </form>
          <button onClick={() => navigate("/")} className="mt-4 w-full text-gray-500 hover:text-gray-300 text-sm text-center transition">← Back to app</button>
        </div>
      </div>
    );
  }

  const keys = keypool?.keys || [];
  const availableKeys = keys.filter((k) => k.available).length;
  const totalKeys = keypool?.total_keys ?? 0;
  const allDead = availableKeys === 0 && totalKeys > 0;
  const someDown = availableKeys < totalKeys && availableKeys > 0;

  // Map quota data by key suffix for quick lookup
  const quotaByKey = {};
  if (quotaData?.keys) {
    for (const qk of quotaData.keys) {
      quotaByKey[qk.key_suffix] = qk;
    }
  }
  const aggDaily = quotaData?.aggregate_daily || {};

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🛡️</span>
          <div>
            <h1 className="text-xl font-bold">Pixtection Admin</h1>
            <p className="text-gray-400 text-xs">ai-detect.teq.la</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <span className="text-gray-500 text-xs hidden sm:block">{lastRefresh ? `Updated ${lastRefresh.toLocaleTimeString()}` : ""}</span>
          <button onClick={loadData} disabled={loading} className="bg-gray-800 hover:bg-gray-700 text-sm px-3 py-1.5 rounded-lg transition">{loading ? "⟳" : "↻"}</button>
          <button onClick={() => navigate("/")} className="bg-gray-800 hover:bg-gray-700 text-sm px-3 py-1.5 rounded-lg transition">← App</button>
          <button onClick={handleLogout} className="bg-red-900 hover:bg-red-800 text-sm px-3 py-1.5 rounded-lg transition">Logout</button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 space-y-8">
        {msg && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-sm flex justify-between items-start gap-2">
            <span className="break-words">{msg}</span>
            <button onClick={() => setMsg("")} className="text-gray-500 hover:text-white shrink-0">✕</button>
          </div>
        )}

        {allDead && (
          <div className="bg-red-950 border border-red-700 rounded-xl px-5 py-4 flex items-start gap-3">
            <span className="text-2xl shrink-0">🚨</span>
            <div>
              <p className="text-red-300 font-semibold">All API keys are rate-limited!</p>
              <p className="text-red-400 text-sm mt-1">Users will see errors. Click <strong>📊 Fetch Quota Status</strong> to see live quota details. Keys auto-recover after cooldown.</p>
            </div>
          </div>
        )}

        {/* Stats */}
        <section>
          <h2 className="text-base font-semibold text-gray-400 uppercase tracking-wider mb-4">📊 Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Analyses" value={stats?.total_analyses} color="blue" />
            <StatCard label="AI Detected" value={stats?.verdicts?.AI} color="red" />
            <StatCard label="Real Images" value={stats?.verdicts?.REAL} color="green" />
            <StatCard label="Uncertain" value={stats?.verdicts?.UNCERTAIN} color="yellow" />
          </div>
        </section>

        {/* Quota Status */}
        <section>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="text-base font-semibold text-gray-400 uppercase tracking-wider">📊 API Quota Status</h2>
            <button onClick={handleFetchQuota} disabled={fetchingQuota}
              className={`flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg transition ${
                fetchingQuota ? "bg-gray-700 text-gray-400 cursor-not-allowed" : "bg-purple-700 hover:bg-purple-600 text-white"
              }`}>
              {fetchingQuota ? <><span className="animate-spin inline-block">⟳</span> Fetching quota…</> : <>📊 Fetch Quota Status</>}
            </button>
          </div>

          {quotaData ? (
            <div className="space-y-4">
              {/* Aggregate pool bar */}
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-white">Daily Request Pool</h3>
                    <p className="text-xs text-gray-500">{totalKeys} keys × {quotaData.model} ({quotaData.tier} tier)</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">
                      <span className="text-red-400">{aggDaily.used ?? "?"}</span>
                      <span className="text-gray-500"> / </span>
                      <span className="text-white">{aggDaily.limit ?? "?"}</span>
                      <span className="text-gray-500 text-xs ml-1">used</span>
                    </p>
                    <p className={`text-xs font-semibold ${
                      aggDaily.remaining === 0 ? "text-red-400" :
                      (aggDaily.remaining ?? 0) < 20 ? "text-yellow-400" : "text-green-400"
                    }`}>
                      {aggDaily.remaining != null ? `${aggDaily.remaining} remaining` : "—"}
                    </p>
                  </div>
                </div>
                <QuotaBar label="Total Pool Usage" used={aggDaily.used} limit={aggDaily.limit} remaining={aggDaily.remaining} />

                {/* Summary row */}
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-green-950/30 border border-green-900 rounded-lg py-2">
                    <p className="text-green-400 text-xl font-bold">{quotaData.keys_alive}</p>
                    <p className="text-green-500 text-xs">Alive</p>
                  </div>
                  <div className="bg-red-950/30 border border-red-900 rounded-lg py-2">
                    <p className="text-red-400 text-xl font-bold">{quotaData.keys_daily_exhausted}</p>
                    <p className="text-red-500 text-xs">Daily Exhausted</p>
                  </div>
                  <div className="bg-yellow-950/30 border border-yellow-900 rounded-lg py-2">
                    <p className="text-yellow-400 text-xl font-bold">{quotaData.keys_rpm_limited}</p>
                    <p className="text-yellow-500 text-xs">RPM Limited</p>
                  </div>
                </div>
                <p className="text-gray-600 text-xs">Fetched at: {quotaData.fetched_at || "—"}</p>
              </div>
            </div>
          ) : (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
              <p className="text-gray-500 text-sm mb-3">Click <strong>📊 Fetch Quota Status</strong> to query each Gemini API key's live quota</p>
              <p className="text-gray-600 text-xs">Sends 1 tiny request per key. If key succeeds → quota OK. If 429 → parses exact limits.</p>
            </div>
          )}
        </section>

        {/* Key Pool */}
        <section>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-semibold text-gray-400 uppercase tracking-wider">🔑 Key Pool</h2>
              <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                allDead ? "bg-red-900 text-red-300" : someDown ? "bg-yellow-900 text-yellow-300" : "bg-green-900 text-green-300"
              }`}>{availableKeys}/{totalKeys} available</span>
            </div>
            <button
              onClick={() => setShowKeyInput((v) => !v)}
              className="flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-blue-700 hover:bg-blue-600 text-white transition"
            >
              {showKeyInput ? "✕ Cancel" : "＋ Add Key"}
            </button>
          </div>

          {/* Add key input */}
          {showKeyInput && (
            <div className="bg-gray-900 border border-blue-800 rounded-xl p-4 mb-4">
              <p className="text-gray-400 text-sm mb-3">Add a new Gemini API key to the pool. It will be saved to the server config and available immediately.</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  placeholder="AIzaSy..."
                  className="flex-1 bg-gray-800 text-white rounded-lg px-4 py-2.5 font-mono text-sm border border-gray-700 focus:outline-none focus:border-blue-500 transition"
                  onKeyDown={(e) => e.key === "Enter" && handleAddKey()}
                  autoFocus
                />
                <button
                  onClick={handleAddKey}
                  disabled={addingKey || !newKey.trim()}
                  className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition ${
                    addingKey || !newKey.trim()
                      ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                      : "bg-green-700 hover:bg-green-600 text-white"
                  }`}
                >
                  {addingKey ? "Adding…" : "Add Key"}
                </button>
              </div>
            </div>
          )}

          {totalKeys > 0 && !quotaData && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl px-5 py-4 mb-4 grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Available</span><span>{availableKeys}/{totalKeys}</span></div>
                <Bar pct={(availableKeys / totalKeys) * 100} color={allDead ? "bg-red-500" : someDown ? "bg-yellow-500" : "bg-green-500"} height="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Probed Healthy</span><span>{keys.filter(k => k.quota_ok === true).length}/{totalKeys}</span></div>
                <Bar pct={(keys.filter(k => k.quota_ok === true).length / totalKeys) * 100} color="bg-blue-500" height="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Success Rate</span></div>
                <Bar pct={keys.reduce((s, k) => s + (k.total_requests || 0), 0) > 0
                  ? Math.round(keys.reduce((s, k) => s + (k.success_count || 0), 0) / Math.max(1, keys.reduce((s, k) => s + (k.total_requests || 0), 0)) * 100) : 0}
                  color="bg-purple-500" height="h-2" />
              </div>
            </div>
          )}

          <div className="space-y-2">
            {keys.map((k) => (
              <KeyCard
                key={k.key_suffix}
                k={k}
                quotaData={quotaByKey[k.key_suffix] || null}
                onRemove={handleRemoveKey}
                removing={removingKey === k.key_suffix.replace(/^\.+/, "")}
              />
            ))}
          </div>
          <p className="text-gray-700 text-xs mt-3">
            Auto-refresh every 30s · Click a key to expand · Use 📊 Fetch Quota Status for live quota data
          </p>
        </section>

        {/* Actions */}
        <section>
          <h2 className="text-base font-semibold text-gray-400 uppercase tracking-wider mb-4">⚙️ Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-5">
              <h3 className="font-semibold text-white mb-1">🗑️ Purge Image Payloads</h3>
              <p className="text-gray-400 text-sm mb-4">Remove stored image bytes. Auto-runs hourly.</p>
              <button onClick={handlePurge} className="bg-yellow-700 hover:bg-yellow-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition">Run Purge Now</button>
            </div>
            <div className="bg-gray-900 rounded-xl border border-red-900 p-5">
              <h3 className="font-semibold text-white mb-1">💀 Clear All History</h3>
              <p className="text-gray-400 text-sm mb-4">Permanently delete all records. Cannot be undone.</p>
              <button onClick={handleClearHistory} className="bg-red-800 hover:bg-red-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition">Delete All Records</button>
            </div>
          </div>
        </section>

        {/* System */}
        <section>
          <h2 className="text-base font-semibold text-gray-400 uppercase tracking-wider mb-4">🖥️ System</h2>
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><p className="text-gray-500 text-xs mb-1">Server Time</p><p className="text-white font-mono text-xs">{stats?.server_time ? new Date(stats.server_time).toLocaleString() : "—"}</p></div>
            <div><p className="text-gray-500 text-xs mb-1">Gemini Model</p><p className="text-white font-mono text-xs">{quotaData?.model || "gemini-2.5-pro"}</p></div>
            <div><p className="text-gray-500 text-xs mb-1">Tier</p><p className="text-white font-mono text-xs">{quotaData?.tier || "free"}</p></div>
            <div><p className="text-gray-500 text-xs mb-1">Key Cooldown</p><p className="text-white font-mono text-xs">60s per 429</p></div>
          </div>
        </section>
      </div>
    </div>
  );
}